export interface TableShapeIconInfo {
  iconName: string;
  description: string;
}

/**
 * Derives an array of icon names from API table shapes (e.g. from useTableShapes).
 */
export const getIconNamesFromTableShapes = (
  tableShapes: Array<{ iconName: string }>,
): string[] =>
  [...new Set(tableShapes.map((s) => s.iconName))].sort((a, b) =>
    a.localeCompare(b),
  );

/**
 * Derives a map of icon name → human-readable description from API table shapes.
 */
export const getHumanReadableIconNamesFromTableShapes = (
  tableShapes: TableShapeIconInfo[],
): Record<string, string> =>
  Object.fromEntries(tableShapes.map((s) => [s.iconName, s.description]));

export const getDefaultTableMetadata = (iconName: string) => {
  const optionalTableContent = {
    description: null as string | null,
    tags: [] as string[],
    images: [] as Array<{ id: string; imageKey: string; position: number }>,
  };

  switch (iconName) {
    // 1 tops
    case 'NoTableChairB':
    case 'NoTableChairC':
    case 'NoTableChairD':
    case 'NoTableChairE':
    case 'NoTableChairF':
    case 'NoTableChairG':
    case 'Round11A':
    case 'Round11B':
    case 'Square11A':
    case 'Square11B':
    case 'YogaMat':
      return {
        ...optionalTableContent,
        minimumGuests: 1,
        maximumGuests: 1,
        turnTime: 60,
      };
    // 2 tops
    case 'NoTableBedA':
    case 'NoTableBedB':
    case 'NoTableBenchA':
    case 'NoTableBenchB':
    case 'NoTableCabanaA':
    case 'NoTableCouchA':
    case 'NoTableNotUserA':
    case 'Oval21A':
    case 'Oval21B':
    case 'Oval21C':
    case 'Oval21D':
    case 'Oval22A':
    case 'Rectangle21B':
    case 'Rectangle21C':
    case 'Rectangle21D':
    case 'Rectangle21E':
    case 'Round21A':
    case 'Round22A':
    case 'Round22B':
    case 'Round22C':
    case 'Round22D':
    case 'Square21A':
    case 'Square21B':
    case 'Square21C':
    case 'Square21D':
    case 'Square22A':
    case 'Square22C':
    case 'Square22D':
    case 'Square22E':
    case 'Square42B': // issue
      return {
        ...optionalTableContent,
        minimumGuests: 1,
        maximumGuests: 2,
        turnTime: 60,
      };
    // 3 tops
    case 'Oval31A':
    case 'Oval31B':
    case 'Oval31C':
    case 'Rectangle31A':
    case 'Rectangle31B':
    case 'Rectangle32A':
    case 'Rectangle32B':
    case 'Rectangle32C':
    case 'Rectangle33A':
    case 'Round22E':
    case 'Round33A':
    case 'Round33B':
    case 'Square32A':
      return {
        ...optionalTableContent,
        minimumGuests: 2,
        maximumGuests: 3,
        turnTime: 90,
      };
    // 4 tops
    case 'NoTableBenchC':
    case 'Oval42A':
    case 'Oval42B':
    case 'Oval42C':
    case 'Oval42D':
    case 'Oval43A':
    case 'Oval43B':
    case 'Rectangle42A':
    case 'Rectangle42B':
    case 'Rectangle42C':
    case 'Rectangle42D':
    case 'Rectangle42E':
    case 'Rectangle42F':
    case 'Rectangle42H':
    case 'Rectangle42I':
    case 'Rectangle43A':
    case 'Rectangle43B':
    case 'Rectangle43C':
    case 'Round42A':
    case 'Round42B':
    case 'Round44A':
    case 'Round44B':
    case 'Round44C':
    case 'Round44D':
    case 'Square42A':
    case 'Square43A':
    case 'Square44A':
    case 'Square44B':
      return {
        ...optionalTableContent,
        minimumGuests: 2,
        maximumGuests: 4,
        turnTime: 90,
      };
    // 5 tops
    case 'NoTableCouchB':
    case 'Oval53A':
    case 'Oval53B':
    case 'Oval53C':
    case 'Oval53D':
    case 'Rectangle52B':
    case 'Rectangle53A':
    case 'Rectangle54A':
    case 'Rectangle54B':
    case 'Rectangle53D':
    case 'Rectangle53B':
    case 'Round52A':
    case 'Round52B':
    case 'Round53A':
    case 'Round53B':
    case 'Round55A':
    case 'Round55B':
    case 'Round84C':
    case 'Square52A':
    case 'Square53A':
    case 'Square54A':
      return {
        ...optionalTableContent,
        minimumGuests: 3,
        maximumGuests: 5,
        turnTime: 120,
      };
    // 6 tops
    case 'Oval61A':
    case 'Oval62A':
    case 'Oval62B':
    case 'Oval62C':
    case 'Oval62D':
    case 'Oval62E':
    case 'Oval64A':
    case 'Rectangle62A':
    case 'Rectangle62B':
    case 'Rectangle62C':
    case 'Rectangle62D':
    case 'Rectangle62E':
    case 'Rectangle63A':
    case 'Rectangle64A':
    case 'Rectangle64B':
    case 'Rectangle64C':
    case 'Rectangle64D':
    case 'Round62A':
    case 'Round63A':
    case 'Round63B':
    case 'Round64A':
    case 'Square63A':
    case 'Square63B':
    case 'Square63C':
    case 'Square63D':
    case 'Square64A':
    case 'Square64B':
      return {
        ...optionalTableContent,
        minimumGuests: 4,
        maximumGuests: 6,
        turnTime: 120,
      };
    // 7tops
    case 'Square73A':
    case 'Rectangle73A':
    case 'Rectangle73B':
      return {
        ...optionalTableContent,
        minimumGuests: 4,
        maximumGuests: 7,
        turnTime: 150,
      };
    // 8tops
    case 'Oval84A':
    case 'Oval84B':
    case 'Oval84C':
    case 'Oval84D':
    case 'Rectangle82A':
    case 'Round82A':
    case 'Round84A':
    case 'Round84B':
    case 'Square83A':
    case 'Square84A':
    case 'Square84B':
      return {
        ...optionalTableContent,
        minimumGuests: 6,
        maximumGuests: 8,
        turnTime: 150,
      };
    // 10tops
    case 'Rectangle104A':
      return {
        ...optionalTableContent,
        minimumGuests: 8,
        maximumGuests: 10,
        turnTime: 180,
      };
    // 11tops
    case 'Rectangle113A':
      return {
        ...optionalTableContent,
        minimumGuests: 8,
        maximumGuests: 11,
        turnTime: 210,
      };
    // 12tops
    case 'Rectangle124A':
    case 'Square123A':
    case 'Square123B':
      return {
        ...optionalTableContent,
        minimumGuests: 8,
        maximumGuests: 12,
        turnTime: 210,
      };
    // 16tops
    case 'Rectangle164A':
      return {
        ...optionalTableContent,
        minimumGuests: 12,
        maximumGuests: 16,
        turnTime: 210,
      };
    // 20tops
    case 'Rectangle202A':
      return {
        ...optionalTableContent,
        minimumGuests: 15,
        maximumGuests: 20,
        turnTime: 210,
      };
    // 30tops
    case 'Rectangle304A':
      return {
        ...optionalTableContent,
        minimumGuests: 20,
        maximumGuests: 30,
        turnTime: 210,
      };
    default:
      return {
        ...optionalTableContent,
        minimumGuests: 1,
        maximumGuests: 4,
        turnTime: 90,
      };
  }
};

export interface ListingId {
  id: string;
}

export enum TableRating {
  Economy = -1,
  Standard = 0,
  StandardPlus = 1,
  Premium = 2,
  PremiumPlus = 3,
  Elite = 4,
}

export interface FloorPlanTableData {
  iconName: string;
  iconWidthScale: string;
  id: string;
  left: string;
  maximumGuests: number;
  minimumGuests: number;
  name: string;
  orientation: string;
  pulse?: boolean;
  rating: TableRating;
  listings: ListingId[];
  top: string;
  turnTime: number;
}

export interface TableShape {
  id: string;
  iconName: string;
  description: string;
  defaultMinGuest: number;
  defaultMaxGuest: number;
  defaultTurnTime: number;
}

export interface FloorPlanData {
  id: string;
  backgroundSrc?: string;
  name: string;
  floorPlanTables: FloorPlanTableData[];
}

export interface FloorPlanSnapshotTableData {
  iconName: string;
  iconWidthScale: string;
  id: string;
  isHighlighted: boolean;
  left: string;
  name: string;
  orientation: string;
  top: string;
}

export interface FloorPlanSnapshotData {
  backgroundSrc: string;
  floorPlanTables: FloorPlanSnapshotTableData[];
}
